<template>
  <div class="sleep-media-viewer">
    <!-- Image content -->
    <CloudflareImagePreview
      v-if="isImageContent && (item.cover_photo || item.main_photo)"
      :src="item.cover_photo || item.main_photo"
      :alt="item.title"
      :fit="fit"
    />
    
    <!-- Video content -->
    <CloudflareVideoPreview
      v-else-if="isVideoContent && (item.video_over || item.video_over_ar)"
      :src="getVideoSource"
      :poster="item.cover_photo || item.main_photo"
      :muted="muted"
      :autoplay="autoplay"
      :showControls="showControls"
    />
    
    <!-- Audio content -->
    <div v-else-if="isAudioContent" class="audio-container">
      <img v-if="item.cover_photo || item.main_photo" 
           :src="item.cover_photo || item.main_photo" 
           :alt="item.title" 
           class="audio-thumbnail"
      />
      <div class="audio-player">
        <audio 
          :src="item.audio_url || item.audio_url_ar" 
          :controls="showControls" 
          :autoplay="autoplay"
        >
          Your browser does not support the audio element.
        </audio>
      </div>
    </div>
    
    <!-- Fallback for items with no media -->
    <div v-else class="no-media-placeholder">
      <div class="placeholder-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M15 6H9a6 6 0 0 0 0 12h6a6 6 0 0 0 0-12z"></path>
          <path d="M9 12l6 0"></path>
        </svg>
      </div>
      <p class="placeholder-text">No media available</p>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue';
import { CloudflareImagePreview, CloudflareVideoPreview } from '@/components/media';

export default defineComponent({
  name: 'SleepMediaViewer',
  components: {
    CloudflareImagePreview,
    CloudflareVideoPreview
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    fit: {
      type: String,
      default: 'cover' // 'contain', 'cover', 'scale-down'
    },
    muted: {
      type: Boolean,
      default: true
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    showControls: {
      type: Boolean,
      default: true
    },
    useArabic: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    // Determine the content type
    const isVideoContent = computed(() => {
      return props.item.display_type === 'video' || 
             (props.item.video_over || props.item.video_over_ar);
    });
    
    const isAudioContent = computed(() => {
      return props.item.type === 'bedtime' || 
             ((props.item.audio_url || props.item.audio_url_ar) && !isVideoContent.value);
    });
    
    const isImageContent = computed(() => {
      return !isVideoContent.value && !isAudioContent.value;
    });
    
    // Get the appropriate video source based on language preference
    const getVideoSource = computed(() => {
      if (props.useArabic && props.item.video_over_ar) {
        return props.item.video_over_ar;
      }
      return props.item.video_over || props.item.audio_url;
    });
    
    return {
      isVideoContent,
      isAudioContent,
      isImageContent,
      getVideoSource
    }
  }
});
</script>

<style scoped>
.sleep-media-viewer {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  background-color: #f3f4f6;
}

.audio-container {
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f3f4f6;
}

.audio-thumbnail {
  width: 100%;
  height: auto;
  object-fit: cover;
  max-height: 220px;
}

.audio-player {
  width: 100%;
  background: linear-gradient(0deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%);
  padding: 8px;
  position: absolute;
  bottom: 0;
  left: 0;
}

.audio-player audio {
  width: 100%;
  border-radius: 4px;
}

.no-media-placeholder {
  width: 100%;
  height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f3f4f6;
  color: #9ca3af;
}

.placeholder-icon {
  margin-bottom: 12px;
}

.placeholder-text {
  font-size: 14px;
  font-weight: 500;
}
</style> 